var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { ChapterModel } from './models/chapter.model';
import { plainToClass, classToClass } from 'class-transformer';
import { uuid } from '../shared/uuid';
import { SerializeChapterService } from 'src/app/edit/serialize/chapter/serialize-chapter.service';
import { BackgroundModel } from '../engine/models/background.model';
import { LabelsService } from './labels.service';
import { omit, isEmpty } from 'lodash';
import { LabelThemeEnum } from './models/labels/label-theme.enum';
import { HotspotThemesEnum } from './models/labels/hotspot.themes.enum';
import * as i0 from "@angular/core";
import * as i1 from "../edit/serialize/chapter/serialize-chapter.service";
import * as i2 from "./labels.service";
var ChapterService = /** @class */ (function () {
    function ChapterService(serializeChapterService, labelService) {
        this.serializeChapterService = serializeChapterService;
        this.labelService = labelService;
    }
    // Update engine with added chapter
    ChapterService.prototype.addChapterToEngine = function (chapter, insertIndex) {
        // get chapter data from chapter model
        var serialized = this.serializeChapterService.serializeSingleChapter(chapter, true);
        var chapterData = serialized.chapter;
        // copy the synchronization to the chapterData before building branch
        chapterData.synchronization = JSON.parse(JSON.stringify(chapter.synchronization));
        // create the branch from modificed data and add to timeline
        var branch = new Human.timeline.BranchTimeline(chapterData);
        Human.timeline.activeRoot.addChapter(branch, { rebuild: true, position: insertIndex });
    };
    // Update engine to remove chapter. This does not update application state, that 
    // is handled separately in story.state.ts
    ChapterService.prototype.removeChapterFromEngine = function (chapter) {
        Human.timeline.activeRoot._chapters = Human.timeline.activeRoot._chapters
            .filter(function (_chapter) { return _chapter.id !== chapter.id; });
        Human.timeline.activeRoot.build();
    };
    // make a copy of the chapter model
    ChapterService.prototype.copyChapter = function (chapter) {
        // new object and new identifications
        var chapterCopy = classToClass(chapter);
        // generate new id
        chapterCopy.id = uuid.idWithPrefix('chapter');
        // generate new ids for each label
        chapterCopy.labels.forEach(function (label) {
            label.id = uuid.idWithPrefix('label');
            label.chapterId = chapterCopy.id;
        });
        // copy ready state
        chapterCopy.readyState = { model: true, animation: true };
        return chapterCopy;
    };
    // return the curent chapter id
    ChapterService.prototype.getCurrentChapterId = function () {
        return Human.timeline.activeRoot._nowBranch.id;
    };
    // replace engine chapter with chapter model
    ChapterService.prototype.replaceEngineChapterWithStateChapter = function (chapter) {
        var humanChapter = Human.timeline.branchTimelines[chapter.id];
        if (!humanChapter) {
            return;
        }
        // swap stateful data
        humanChapter.synchronization = JSON.parse(JSON.stringify(chapter.synchronization));
    };
    // relay chapter synchronization data to the engine's record of the chapter
    ChapterService.prototype.updateChapterSynchronization = function (chapterId, synchronization) {
        Human.timeline.branchTimelines[chapterId].synchronization = synchronization;
    };
    // clear color mods from synchronization on specified objects
    ChapterService.prototype.clearColorModsFromSync = function (chapterSync, objectIds) {
        objectIds.forEach(function (id) {
            var sync = omit(chapterSync[id] || {}, ['tintColor', 'contrast', 'brightness', 'saturation', 'opacity']);
            if (!isEmpty(sync)) {
                chapterSync[id] = sync;
            }
            else {
                delete chapterSync[id];
            }
        });
        return chapterSync;
    };
    // clear bool state from synchronization on specified objects
    ChapterService.prototype.clearBoolStateFromSync = function (chapterSync, objectIds, property) {
        objectIds.forEach(function (id) {
            var sync = omit(chapterSync[id] || {}, [property]);
            if (!isEmpty(sync)) {
                chapterSync[id] = sync;
            }
            else {
                delete chapterSync[id];
            }
        });
        return chapterSync;
    };
    // create chapterModel from human branch objects
    ChapterService.prototype.fromHumanChapter = function (humanModuleData, storyType, humanChapter, idx) {
        var _this = this;
        // work with a copy;
        var chapterData = __assign({}, humanChapter);
        // copy over per chapter environment data form humanModuleData
        chapterData.environment = __assign({}, humanModuleData.chapters[idx].environment);
        // ensure every chapter has an id
        if (!chapterData.id) {
            chapterData.id = uuid.idWithPrefix('chapter');
        }
        // reformat timelinemaps
        var animated = false;
        chapterData.timelineMap = Object.values(humanChapter.maps).map(function (mapData) {
            if (mapData.data.timeline.id !== 'dummy') {
                animated = true;
            }
            return {
                start: mapData.start,
                end: mapData.end,
                data: {
                    timeline: mapData.data.timeline.id,
                    start: mapData.data.start,
                    end: mapData.data.end,
                    fit: mapData.data.fit || false,
                    onEnd: mapData.data.onEnd
                }
            };
        });
        delete chapterData.maps;
        // reformat animation -> ui
        var playCount = 0; // default to scrub
        var looping = humanChapter.animation ? humanChapter.animation.loop : false;
        if (animated && looping) {
            playCount = -1;
        }
        else if (animated && !looping) {
            playCount = 1;
        }
        chapterData.ui = humanChapter.ui && !isEmpty(humanChapter.ui) ? humanChapter.ui : { 'play': { 'count': playCount } };
        // convert annotations to labels
        // #TODO: Mike - Remember to make sure this doesn't break anything
        // const theme = storyType === 'module' ? LabelThemeEnum.StudioNext : LabelThemeEnum.Annotation;
        var theme = LabelThemeEnum.Annotation;
        var annotationLabels = __spread(chapterData.annotations || []).map(function (annotation) {
            var label = _this.labelService.deserializeAnnotation(annotation, chapterData, theme);
            return label;
        });
        // filter and convert hotspots to labels
        var legacyHotspots = [];
        var hotspotGroups = this.groupHotspots(__spread(chapterData.hotspots || []).filter(function (hotspot) {
            var convertable = _this.labelService.filterHotspot(humanModuleData, humanChapter, hotspot);
            if (!convertable) {
                legacyHotspots.push(hotspot);
            }
            else {
                if (!hotspot.theme) {
                    // set default theme by story type
                    hotspot.theme = storyType === 'module' ? HotspotThemesEnum.studioNext : HotspotThemesEnum.classicHotspot;
                }
            }
            return convertable;
        }));
        var hotspotLabels = hotspotGroups.map(function (hotspotGroup) {
            var _a;
            return (_a = _this.labelService).deserializeHotspots.apply(_a, __spread([humanModuleData, humanChapter, idx], hotspotGroup));
        });
        chapterData.labels = __spread(annotationLabels, hotspotLabels);
        delete chapterData.annotations;
        // reformat chapter background
        chapterData.background = BackgroundModel.deserialize(humanChapter.background);
        // return instance of ChapterModel
        var chapter = plainToClass(ChapterModel, chapterData);
        chapter.hotspots = legacyHotspots;
        chapter.readyState = { model: false, animation: false };
        return chapter;
    };
    // two hotspots can be combined into one label 
    ChapterService.prototype.groupHotspots = function (hotspots) {
        var hotspotGroups = {};
        // helper functions to key hotspots that are reciprocal
        var keyPosition = function (hotspot) {
            if (hotspot.primitiveIndex) {
                return hotspot.primitiveIndex.toFixed(0);
            }
            else if (hotspot.pos) {
                return hotspot.pos.map(function (c) { return c.toFixed(3); }).join(':');
            }
            else {
                return Math.random().toFixed(3);
            }
        };
        var keyType = function (type) {
            return RegExp('^circle-(plus|minus)').test(type) ? 'pair' : 'single';
        };
        // group hotspots by key
        hotspots.forEach(function (hotspot) {
            var groupKey = [keyType(hotspot.type), hotspot.objectId, keyPosition(hotspot)].join('|');
            if (groupKey in hotspotGroups) {
                hotspotGroups[groupKey].push(hotspot);
            }
            else {
                hotspotGroups[groupKey] = [hotspot];
            }
        });
        return Object.values(hotspotGroups);
    };
    ChapterService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function ChapterService_Factory() { return new ChapterService(i0.ɵɵinject(i1.SerializeChapterService), i0.ɵɵinject(i2.LabelsService)); }, token: ChapterService, providedIn: "root" });
    return ChapterService;
}());
export { ChapterService };
